import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { buildPreviewRehearsalBody } from "@/lib/previewRehearsalRequest";
import { trackEvent } from "@/lib/analytics";
import { getAttribution } from "@/lib/attribution";
import { getFreePracticeDebrief } from "@/lib/freePracticeDebriefs";

interface Message { role: "user" | "assistant"; content: string }

const RehearsalPreview = ({ primaryPattern, previewVariant }: { primaryPattern: string; previewVariant?: string }) => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [turn, setTurn] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const started = useRef(false);
  const completed = turn === 3;

  const track = (name: string, extra: Record<string, unknown> = {}) => void trackEvent(name, {
    ...getAttribution(), pattern: primaryPattern, source_page: window.location.pathname, ...extra,
  });

  const send = async () => {
    const text = input.trim();
    if (!text || inFlight.current || completed) return;
    inFlight.current = true;
    setLoading(true);
    setError(null);
    if (!started.current) { started.current = true; track("demo_started"); track("practice_demo_started", { turn: 1, reply_length: text.length }); }
    try {
      const { data, error: requestError } = await supabase.functions.invoke("preview-rehearsal", {
        body: buildPreviewRehearsalBody(text, turn + 1, primaryPattern, messages),
      });
      if (requestError || !data?.content) {
        // Friendly limit responses also arrive on non-2xx responses.
        let detail = data;
        if (requestError?.context instanceof Response) {
          try { detail = await requestError.context.json(); } catch { /* no response body */ }
        }
        setError(detail?.busy ? "Demo is busy, sign in to keep practicing" : "Could not generate a reply. Please try again.");
        return;
      }
      setMessages(prior => [...prior, { role: "user", content: text }, { role: "assistant", content: data.content }]);
      setInput("");
      const next = turn + 1;
      setTurn(next);
      // Never include message text — only turn numbers and lengths.
      track("practice_demo_turn", { turn: next, reply_length: text.length });
      if (next === 3) { track("demo_completed"); track("practice_demo_completed", { turn: 3 }); }
    } catch {
      setError("Could not generate a reply. Please try again.");
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };

  const continuePractice = () => {
    if (authLoading) return;
    track("demo_cta_clicked");
    // /login preserves its current address through email and Google sign-in.
    // Its return-page effect then opens this explicit practice destination.
    if (user) {
      navigate("/rehearsal", { state: { entry_type: "free_preview", patterns: [{ name: primaryPattern }] } });
    } else {
      sessionStorage.setItem("greyrock_practice_pattern", primaryPattern);
      navigate("/login?next=rehearsal");
    }
  };

  return (
    <section aria-label="Practice your reply" className="w-full space-y-4 border-t border-border pt-6 text-foreground">
      <div>
        <h2 className="text-xl font-bold font-sans">Practice your reply</h2>
        <p className="mt-1 text-sm text-muted-foreground">Three turns, free. See how this pattern responds when you hold your ground.</p>
      </div>
      {messages.length > 0 && (
        <div role="log" aria-live="polite" className="max-h-80 overflow-y-auto space-y-3">
          {messages.map((message, index) => (
            <div key={index} className={message.role === "user" ? "ml-6 border-l-2 border-primary pl-3" : "mr-6 border-l-2 border-border pl-3"}>
              <p className="text-xs font-mono text-muted-foreground">{message.role === "user" ? "You" : "Them (simulated)"}</p>
              <p className="text-sm leading-relaxed break-words whitespace-pre-wrap">{message.content}</p>
            </div>
          ))}
        </div>
      )}
      {!completed && (
        <div className="space-y-2">
          <label htmlFor="practice-reply" className="text-xs text-muted-foreground">Your reply · Turn {turn + 1} of 3</label>
          <textarea id="practice-reply" aria-label="Your practice reply" value={input} maxLength={1000} rows={3}
            onChange={event => setInput(event.target.value)} disabled={loading}
            placeholder="I’m willing to talk about what happened, but not to debate my feelings."
            className="w-full rounded-md border border-input bg-background p-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50" />
          <p className="text-xs text-muted-foreground">Practice replies are sent to our AI so it can respond. The message you analyzed stays in your browser.</p>
          <Button onClick={send} disabled={loading || !input.trim()} className="gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {loading ? "Responding…" : "Send reply"}
          </Button>
        </div>
      )}
      {error && <p role="alert" className="text-sm text-muted-foreground">{error}</p>}
      {completed && (
        <section aria-label="What to notice" className="rounded-md border border-border bg-card p-4 space-y-2">
          <h3 className="text-base font-semibold">What to notice</h3>
          <p className="text-sm leading-relaxed text-muted-foreground">{getFreePracticeDebrief(previewVariant, primaryPattern)}</p>
        </section>
      )}
      {completed && (
        <div className="space-y-3 border-t border-border pt-4">
          <p className="font-semibold">Keep practicing with full Practice Conversations</p>
          <Button onClick={continuePractice} disabled={authLoading}>Sign in to continue</Button>
        </div>
      )}
    </section>
  );
};

export default RehearsalPreview;
